import { Fragment, useMemo, type ReactNode } from 'react';
import { Heading, type HeadingLevel } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { isExternalHref, parseMarkdown, type BlockNode, type InlineNode } from './markdown';
import './MarkdownContent.css';

export interface MarkdownContentProps {
  /** Markdown source (the subset of ./markdown.ts). */
  source: string;
  /**
   * Lowest heading level used for the document's own headings. The page owns
   * the h1, so `#`/`##` headings start at this level (default 2).
   */
  baseLevel?: HeadingLevel;
  /** Accessible suffix for links that open in a new tab, e.g. "(opens in a new tab)". */
  newTabLabel?: string;
  className?: string;
}

function renderInline(nodes: InlineNode[], newTabLabel: string | undefined): ReactNode[] {
  return nodes.map((node, i) => {
    switch (node.type) {
      case 'text':
        return <Fragment key={i}>{node.text}</Fragment>;
      case 'break':
        return <br key={i} />;
      case 'code':
        return <code key={i} className="rg-markdown__code">{node.text}</code>;
      case 'strong':
        return <strong key={i}>{renderInline(node.children, newTabLabel)}</strong>;
      case 'em':
        return <em key={i}>{renderInline(node.children, newTabLabel)}</em>;
      case 'link': {
        const external = isExternalHref(node.href);
        return (
          <a
            key={i}
            href={node.href}
            className="rg-markdown__link"
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {renderInline(node.children, newTabLabel)}
            {external && newTabLabel ? <span className="rg-markdown__sr-only"> {newTabLabel}</span> : null}
          </a>
        );
      }
      default:
        return null;
    }
  });
}

function clampLevel(level: number): HeadingLevel {
  return Math.min(6, Math.max(1, level)) as HeadingLevel;
}

function renderBlocks(blocks: BlockNode[], baseLevel: HeadingLevel, newTabLabel: string | undefined): ReactNode[] {
  // The shallowest heading in the document maps to baseLevel; deeper ones keep their relative depth.
  const minLevel = Math.min(7, ...blocks.filter((b) => b.type === 'heading').map((b) => (b as { level: number }).level));
  const shift = baseLevel - (minLevel === 7 ? baseLevel : minLevel);

  return blocks.map((block, i) => {
    switch (block.type) {
      case 'heading':
        return (
          <Heading key={i} level={clampLevel(block.level + shift)} className="rg-markdown__heading">
            {renderInline(block.children, newTabLabel)}
          </Heading>
        );
      case 'paragraph':
        return (
          <Text key={i} className="rg-markdown__paragraph">
            {renderInline(block.children, newTabLabel)}
          </Text>
        );
      case 'list': {
        const items = block.items.map((item, j) => (
          <li key={j} className="rg-markdown__list-item">{renderInline(item, newTabLabel)}</li>
        ));
        return block.ordered ? (
          <ol key={i} className="rg-markdown__list">{items}</ol>
        ) : (
          <ul key={i} className="rg-markdown__list">{items}</ul>
        );
      }
      case 'table':
        return (
          // Fixed layout + wrapping keeps tables within the column at 320 px (WCAG 1.4.10).
          <div key={i} className="rg-markdown__table-wrap">
            <table className="rg-markdown__table">
              <thead>
                <tr>
                  {block.header.map((cell, c) => (
                    <th key={c} scope="col">{renderInline(cell, newTabLabel)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c}>{renderInline(cell, newTabLabel)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'blockquote':
        return (
          <blockquote key={i} className="rg-markdown__blockquote">
            {renderBlocks(block.children, baseLevel, newTabLabel)}
          </blockquote>
        );
      case 'hr':
        return <hr key={i} className="rg-markdown__hr" />;
      default:
        return null;
    }
  });
}

/**
 * Renders untyped markdown (the legal documents, the KI-Becken-Mentor's AI
 * answers) as React elements built from design-system atoms. No HTML string is ever injected:
 * raw HTML in the source is shown as text, and only http(s)/mailto/same-site
 * links are linked; external links open in a new tab with rel="noopener noreferrer".
 */
export function MarkdownContent({ source, baseLevel = 2, newTabLabel, className = '' }: MarkdownContentProps) {
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  return (
    <div className={['rg-markdown', className].filter(Boolean).join(' ')}>
      {renderBlocks(blocks, baseLevel, newTabLabel)}
    </div>
  );
}

export default MarkdownContent;
